import { useEffect, useMemo, useState } from 'react';
import { Button, Input, formatPhone, onlyDigits, stripCountryCode } from '@agendor/ui';
import { FormGroup, HelperText, InputGroup } from '../components/Input';
import { Modal } from '../components/Modal';
import BusinessService, {
    InvalidVerificationCodeError,
    PhoneNumberAlreadyInUseError,
    VerificationRateLimitedError,
} from '../business-service';
import { useToast } from '../toast-context';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => Promise<void> | void;
    service: BusinessService;
    currentOwnerPhoneNumber: string;
}

export function OwnerPhoneChangeModal({ isOpen, onClose, onSuccess, service, currentOwnerPhoneNumber }: Props) {
    const { showToast } = useToast();
    const [phoneInput, setPhoneInput] = useState('');
    const [verifyingPhoneNumber, setVerifyingPhoneNumber] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPhoneInput(formatPhone(stripCountryCode(currentOwnerPhoneNumber)));
            setVerifyingPhoneNumber(null);
            setVerificationCode('');
        }
    }, [isOpen, currentOwnerPhoneNumber]);

    const canonicalPhoneInput = useMemo(() => {
        const digits = onlyDigits(phoneInput);
        if (digits.length < 10) return null;
        return digits.length === 13 && digits.startsWith('55') ? digits : '55' + digits;
    }, [phoneInput]);

    const reportError = (error: unknown, fallback: string) => {
        if (error instanceof PhoneNumberAlreadyInUseError) {
            showToast('Este número de WhatsApp já está em uso.', 'error', 'modal');
            return;
        }
        if (error instanceof VerificationRateLimitedError) {
            showToast('Muitas tentativas. Aguarde alguns minutos.', 'error', 'modal');
            return;
        }
        if (error instanceof InvalidVerificationCodeError) {
            showToast('Código incorreto.', 'error', 'modal');
            return;
        }
        console.error(fallback, error);
        showToast(fallback, 'error', 'modal');
    };

    const handleRequest = async () => {
        if (!canonicalPhoneInput) {
            showToast('Digite um número de telefone válido.', 'error', 'modal');
            return;
        }
        if (canonicalPhoneInput === currentOwnerPhoneNumber) return;
        setSaving(true);
        try {
            const result = await service.requestOwnerPhoneChange({ phoneNumber: canonicalPhoneInput });
            if (!result.verificationRequired) {
                await onSuccess();
                onClose();
                return;
            }
            setVerifyingPhoneNumber(canonicalPhoneInput);
            setVerificationCode('');
        } catch (error) {
            reportError(error, 'Não foi possível solicitar a alteração.');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirm = async () => {
        if (!verifyingPhoneNumber) return;
        const code = verificationCode.trim();
        if (code.length === 0) {
            showToast('Digite o código recebido por WhatsApp.', 'error', 'modal');
            return;
        }
        setSaving(true);
        try {
            await service.confirmOwnerPhoneChange({ phoneNumber: verifyingPhoneNumber, code });
            await onSuccess();
            onClose();
        } catch (error) {
            if (error instanceof PhoneNumberAlreadyInUseError) {
                setVerifyingPhoneNumber(null);
                setVerificationCode('');
            }
            reportError(error, 'Não foi possível confirmar a alteração.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Alterar WhatsApp"
            data-testid="owner-phone-modal"
            footer={
                <>
                    <Button type="button" onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    {verifyingPhoneNumber === null ? (
                        <Button
                            primary
                            type="submit"
                            form="owner-phone-form"
                            data-testid="owner-phone-request-btn"
                            disabled={
                                saving ||
                                canonicalPhoneInput === null ||
                                canonicalPhoneInput === currentOwnerPhoneNumber
                            }
                        >
                            {saving ? 'Enviando...' : 'Alterar número'}
                        </Button>
                    ) : (
                        <Button
                            primary
                            type="submit"
                            form="owner-phone-form"
                            data-testid="owner-phone-confirm-btn"
                            disabled={saving || verificationCode.length === 0}
                        >
                            {saving ? 'Confirmando...' : 'Confirmar'}
                        </Button>
                    )}
                </>
            }
        >
            <form
                id="owner-phone-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (verifyingPhoneNumber === null) {
                        handleRequest();
                    } else {
                        handleConfirm();
                    }
                }}
            >
                <FormGroup>
                    <InputGroup>
                        Novo número de WhatsApp
                        <Input
                            autoFocus={verifyingPhoneNumber === null}
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(formatPhone(e.target.value))}
                            placeholder="(11) 99999-9999"
                            disabled={saving || verifyingPhoneNumber !== null}
                        />
                        <HelperText>
                            {verifyingPhoneNumber === null
                                ? 'Você receberá um código por WhatsApp neste número. Este passo é dispensado se o número for o mesmo do seu WhatsApp Business.'
                                : 'Para alterar para outro número, cancele e tente novamente.'}
                        </HelperText>
                    </InputGroup>
                    {verifyingPhoneNumber !== null && (
                        <InputGroup>
                            Código de verificação
                            <Input
                                autoFocus
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(onlyDigits(e.target.value).slice(0, 6))}
                                placeholder="000000"
                                inputMode="numeric"
                                disabled={saving}
                            />
                            <HelperText>Enviamos um código por WhatsApp.</HelperText>
                        </InputGroup>
                    )}
                </FormGroup>
            </form>
        </Modal>
    );
}
